import { getEffectiveToday } from '../../utils/systemTime';
import {
  fetchCoursePlacementLabels,
  getStreamDisplayName
} from '../../utils/groupLabels';
import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

// VOTING_READABILITY_FIX_V1


function isCurrentVotingCourse(course, today) {
  if (course?.is_live !== true) return false;

  const semester = course?.semesters;
  const startDate = String(
    semester?.start_date || ''
  ).slice(0, 10);

  if (!startDate) return false;

  const start = new Date(startDate + 'T12:00:00');
  const now = new Date(today + 'T12:00:00');

  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(now.getTime())
  ) {
    return false;
  }

  const configuredWeeks = Number(semester?.week_count);

  const weeks =
    Number.isInteger(configuredWeeks) &&
    configuredWeeks > 0
      ? configuredWeeks
      : 18;

  const end = new Date(start);
  end.setDate(end.getDate() + weeks * 7);

  return start <= now && now < end;
}

async function fetchCourses(session) {
  const today = getEffectiveToday(session);
  const selectFields =
    'id,name,instructor_id,cq_enabled,voting_enabled,qi_enabled,streams_enabled,' +
    'is_live,course_titles(name),semesters(name,start_date,week_count),created_at';

  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  if (session?.isSuperadmin === true) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?select=${selectFields}&order=created_at.desc`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(
        `Loading voting courses failed (${response.status})`
      );
    }

    const rows = await response.json();

    return (Array.isArray(rows) ? rows : [])
      .filter(
        course =>
          course.voting_enabled === true &&
          isCurrentVotingCourse(course, today)
      );
  }

  const instructorId =
    session?.instructorId;

  if (!instructorId) {
    return [];
  }

  const [
    ownedResponse,
    accessResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?instructor_id=eq.${instructorId}&select=${selectFields}&order=created_at.desc`,
      { headers }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?instructor_id=eq.${instructorId}&select=course_id`,
      { headers }
    )
  ]);

  if (!ownedResponse.ok) {
    throw new Error(
      `Loading owned voting courses failed (${ownedResponse.status})`
    );
  }

  if (!accessResponse.ok) {
    throw new Error(
      `Loading shared voting courses failed (${accessResponse.status})`
    );
  }

  const owned =
    await ownedResponse.json();

  const accessRows =
    await accessResponse.json();

  const sharedIds =
    Array.from(
      new Set(
        (Array.isArray(accessRows)
          ? accessRows
          : [])
          .map(row => row.course_id)
          .filter(Boolean)
      )
    );

  let shared = [];

  if (sharedIds.length) {
    const sharedResponse =
      await fetch(
        `${SUPABASE_URL}/rest/v1/courses?id=in.(${sharedIds.join(',')})&select=${selectFields}&order=created_at.desc`,
        { headers }
      );

    if (!sharedResponse.ok) {
      throw new Error(
        `Loading shared voting courses failed (${sharedResponse.status})`
      );
    }

    shared =
      await sharedResponse.json();
  }

  const byId = new Map();

  [
    ...(Array.isArray(owned)
      ? owned
      : []),
    ...(Array.isArray(shared)
      ? shared
      : [])
  ].forEach(course => {
    if (
      course &&
      course.voting_enabled === true &&
      isCurrentVotingCourse(course, today)
    ) {
      byId.set(
        course.id,
        course
      );
    }
  });

  return Array
    .from(byId.values())
    .sort((a, b) =>
      String(b.created_at || '')
        .localeCompare(
          String(a.created_at || '')
        )
    );
}

async function fetchWeeksWithQuestions(courseId, includeEmptyWeeks = false) {
  const cqFilter = includeEmptyWeeks
    ? ''
    : '&cq_enabled=eq.true';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}${cqFilter}&select=id,week_number,class_date&order=week_number.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const weeks = await response.json();

  if (includeEmptyWeeks) {
    return Array.isArray(weeks)
      ? weeks
      : [];
  }

  // For each CQ week, check if it has questions
  const weeksWithQuestions = [];
  for (const week of weeks) {
    const qResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=eq.${week.id}&select=id&limit=1`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    );
    const q = await qResponse.json();
    if (q.length > 0) weeksWithQuestions.push(week);
  }
  return weeksWithQuestions;
}

async function fetchQuestions(
  weekId,
  hasQI
) {
  if (hasQI) {
    /*
     * Normal QI Voting questions:
     * submitted revised questions.
     */
    const assignmentsR =
      await fetch(
        `${SUPABASE_URL}/rest/v1/cq_question_assignments?course_week_id=eq.${weekId}&select=*,cq_submissions(id,question_text,enrolment_id,is_eliminated,cq_votes(vote_count),enrolments(students(student_code,english_name,chinese_name,is_master)))`,
        {
          headers: {
            'apikey':
              SUPABASE_KEY,

            'Authorization':
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      );

    const assignments =
      await assignmentsR.json();

    let revisedQuestions =
      [];

    if (
      Array.isArray(
        assignments
      ) &&
      assignments.length >
        0
    ) {
      const assignmentIds =
        assignments
          .map(
            assignment =>
              assignment.id
          )
          .join(',');

      const improvementsR =
        await fetch(
          `${SUPABASE_URL}/rest/v1/cq_question_improvements?cq_question_assignment_id=in.(${assignmentIds})&submitted=eq.true&select=*`,
          {
            headers: {
              'apikey':
                SUPABASE_KEY,

              'Authorization':
                'Bearer ' +
                SUPABASE_KEY
            }
          }
        );

      const improvements =
        await improvementsR.json();

      revisedQuestions =
        assignments
          .filter(
            assignment =>
              assignment
                ?.cq_submissions
                ?.enrolments
                ?.students
                ?.is_master !==
              true
          )
          .map(
            assignment => {
              const improvement =
                improvements.find(
                  item =>
                    item
                      .cq_question_assignment_id ===
                    assignment.id
                );

              const original =
                assignment
                  .cq_submissions;

              if (!improvement) {
                return null;
              }

              const revisedText =
                String(
                  improvement
                    .revised_question ||
                  ''
                ).trim();

              /*
               * Empty revised questions never
               * reach Voting.
               */
              if (!revisedText) {
                return null;
              }

              return {
                id:
                  original?.id ||
                  assignment.id,

                question_text:
                  revisedText,

                is_eliminated:
                  original
                    ?.is_eliminated ||
                  false,

                cq_votes:
                  original
                    ?.cq_votes ||
                  [],

                enrolments:
                  original
                    ?.enrolments ||
                  null,

                stream:
                  assignment.stream,

                _assignmentId:
                  assignment.id,

                _improvementId:
                  improvement.id,

                _isRevised:
                  true,

                _originName:
                  [
                    original
                      ?.enrolments
                      ?.students
                      ?.english_name,
                    original
                      ?.enrolments
                      ?.students
                      ?.chinese_name
                  ]
                    .filter(Boolean)
                    .join(' / ') ||
                  original
                    ?.enrolments
                    ?.students
                    ?.student_code ||
                  'Unknown student'
              };
            }
          )
          .filter(Boolean);
    }


    /*
     * Instructor-added Voting questions are
     * ordinary cq_submissions, not QI work.
     *
     * They must therefore be explicitly merged
     * into a QI board on reload.
     */
    const manualR =
      await fetch(
        `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=eq.${weekId}&instructor_added=eq.true&select=*,cq_votes(vote_count),enrolments(stream,students(student_code,is_master))&order=submitted_at.asc`,
        {
          headers: {
            'apikey':
              SUPABASE_KEY,

            'Authorization':
              'Bearer ' +
              SUPABASE_KEY
          }
        }
      );

    const manualRows =
      await manualR.json();

    const manualQuestions =
      Array.isArray(
        manualRows
      )
        ? manualRows
            .filter(
              question =>
                question
                  ?.enrolments
                  ?.students
                  ?.is_master !==
                true
            )
            .map(
              question => ({
                ...question,

                stream:
                  question
                    .voting_stream ??
                  question
                    .enrolments
                    ?.stream ??
                  null,

                _isRevised:
                  false,

                _isInstructorAdded:
                  true
              })
            )
        : [];

    return [
      ...revisedQuestions,
      ...manualQuestions
    ];
  }


  /*
   * Ordinary CQ Voting course.
   */
  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=eq.${weekId}&select=*,cq_votes(vote_count),enrolments(stream,students(student_code,is_master))&order=submitted_at.asc`,
      {
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  const data =
    await response.json();

  return Array.isArray(
    data
  )
    ? data
        .filter(
          question =>
            question
              ?.enrolments
              ?.students
              ?.is_master !==
            true
        )
        .map(
          question => ({
            ...question,

            stream:
              question
                .voting_stream ??
              question
                .enrolments
                ?.stream ??
              null
          })
        )
    : [];
}

async function upsertVote(submissionId, newCount) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_votes?on_conflict=cq_submission_id`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        cq_submission_id: submissionId,
        vote_count: newCount,
        updated_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Saving vote failed (${response.status}): ${detail}`
    );
  }
}

async function updateEliminated(submissionId, isEliminated) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?id=eq.${submissionId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ is_eliminated: isEliminated })
    }
  );
}

async function clearVotes(weekId) {
  // Get all submission IDs for this week
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?week_id=eq.${weekId}&select=id`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const submissions = await response.json();
  
  for (const sub of submissions) {
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_votes?cq_submission_id=eq.${sub.id}`,
      {
        method: 'DELETE',
        headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY }
      }
    );
    await fetch(
      `${SUPABASE_URL}/rest/v1/cq_submissions?id=eq.${sub.id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': 'Bearer ' + SUPABASE_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_eliminated: false })
      }
    );
  }
}

async function saveQuestion(
  weekId,
  courseId,
  text,
  studentCode,
  votingStream
) {
  let enrolmentId = null;
  if (studentCode) {
    const sResponse = await fetch(
      `${SUPABASE_URL}/rest/v1/students?student_code=eq.${encodeURIComponent(studentCode)}&select=id`,
      { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
    );
    const students = await sResponse.json();
    if (students[0]) {
      const eResponse = await fetch(
        `${SUPABASE_URL}/rest/v1/enrolments?student_id=eq.${students[0].id}&course_id=eq.${courseId}&select=id&limit=1`,
        { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
      );
      const enrolments = await eResponse.json();
      if (enrolments[0]) enrolmentId = enrolments[0].id;
    }
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({
        week_id: weekId,
        question_text: text,
        enrolment_id: enrolmentId,
        instructor_added: true,
        question_type: 'Critical',
        voting_stream:
          votingStream ?? null
      })
    }
  );
  return response.json();
}

async function updateQuestion(submissionId, text) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/cq_submissions?id=eq.${submissionId}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ question_text: text })
    }
  );
}

async function deleteVotingQuestionRecord(
  question
) {
  /*
   * A revised QI card is the improvement row.
   * Delete that revised-question record without
   * deleting the original student's CQ.
   */
  if (
    question?._isRevised === true
  ) {
    if (!question._improvementId) {
      throw new Error(
        'This revised QI question has no improvement record ID.'
      );
    }

    const response = await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_question_improvements?id=eq.' +
        encodeURIComponent(
          question._improvementId
        ),
      {
        method: 'DELETE',
        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

    if (!response.ok) {
      const detail =
        await response.text();

      throw new Error(
        'Deleting revised QI question failed (' +
          response.status +
          '): ' +
          detail
      );
    }

    return;
  }

  /*
   * Ordinary CQ: delete its votes first,
   * then the question itself.
   */
  const voteResponse = await fetch(
    SUPABASE_URL +
      '/rest/v1/cq_votes?cq_submission_id=eq.' +
      encodeURIComponent(
        question.id
      ),
    {
      method: 'DELETE',
      headers: {
        'apikey':
          SUPABASE_KEY,

        'Authorization':
          'Bearer ' +
          SUPABASE_KEY
      }
    }
  );

  if (!voteResponse.ok) {
    const detail =
      await voteResponse.text();

    throw new Error(
      'Deleting votes failed (' +
        voteResponse.status +
        '): ' +
        detail
    );
  }

  const questionResponse =
    await fetch(
      SUPABASE_URL +
        '/rest/v1/cq_submissions?id=eq.' +
        encodeURIComponent(
          question.id
        ),
      {
        method: 'DELETE',

        headers: {
          'apikey':
            SUPABASE_KEY,

          'Authorization':
            'Bearer ' +
            SUPABASE_KEY
        }
      }
    );

  if (!questionResponse.ok) {
    const detail =
      await questionResponse.text();

    throw new Error(
      'Deleting question failed (' +
        questionResponse.status +
        '): ' +
        detail
    );
  }
}


function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function getCourseName(course) {
  return course.course_titles?.name || course.name || 'Untitled';
}

function getCourseSelectorValue(courseId, stream) {
  return `${courseId}::${stream ?? ''}`;
}

function parseCourseSelectorValue(value) {
  const [courseId, streamPart = ''] = value.split('::');

  return {
    courseId,
    stream: streamPart === '' ? null : Number(streamPart)
  };
}


// Preserve question order while permitting two-row cards.
function planVotingTallPositions(questions, columns, maxRows, tallIds) {
  const occupied = new Set();
  const positions = [];
  let cursor = 0;

  for (const question of questions) {
    const span = tallIds.has(String(question.id)) ? 2 : 1;
    let position = null;

    for (let cell = cursor; cell < columns * maxRows; cell++) {
      const row = Math.floor(cell / columns);
      const column = cell % columns;

      if (row + span > maxRows) continue;

      let free = true;

      for (let extra = 0; extra < span; extra++) {
        if (occupied.has((row + extra) * columns + column)) {
          free = false;
          break;
        }
      }

      if (!free) continue;

      for (let extra = 0; extra < span; extra++) {
        occupied.add((row + extra) * columns + column);
      }

      position = {
        id: question.id,
        row: row + 1,
        column: column + 1,
        span
      };

      cursor = cell + 1;
      break;
    }

    if (!position) return null;
    positions.push(position);
  }

  return positions;
}

// Measure an off-screen copy rather than changing a live card.
// This avoids expansion/contraction feedback loops.
function measureVotingCardFont(card, width, height, scale) {
  if (!card?.querySelector('[data-voting-fit-text="true"]')) {
    return 64;
  }

  const clone = card.cloneNode(true);

  Object.assign(clone.style, {
    position: 'fixed',
    left: '-20000px',
    top: '0',
    width: Math.max(1, width) + 'px',
    height: Math.max(1, height) + 'px',
    padding: Math.max(5, Math.round(16 * scale)) + 'px',
    gridRow: 'auto',
    gridColumn: 'auto',
    visibility: 'hidden',
    pointerEvents: 'none',
    margin: '0'
  });

  document.body.appendChild(clone);

  try {
    const text = clone.querySelector(
      '[data-voting-fit-text="true"]'
    );

    if (!text || text.clientWidth < 1 || text.clientHeight < 1) {
      return 1;
    }

    let low = 1;
    let high = Math.max(1, Math.min(
      64,
      Math.floor(text.clientWidth / 5),
      Math.floor(clone.clientHeight * 0.35)
    ));
    let best = 1;

    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      text.style.fontSize = middle + 'px';

      if (
        text.scrollHeight <= text.clientHeight + 1 &&
        text.scrollWidth <= text.clientWidth + 1
      ) {
        best = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    return best;
  } finally {
    clone.remove();
  }
}

export default function Voting({
  session
}) {
  const [courses, setCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [weekIndex, setWeekIndex] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderMode, setOrderMode] = useState('submission'); // 'submission' | 'votes'
  const [showAddForm, setShowAddForm] = useState(false);
  const [visibleOrigins, setVisibleOrigins] = useState({});
  const [newQuestion, setNewQuestion] = useState({ text: '', studentCode: '' });
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const containerRef = useRef(null);
  const gridRef = useRef(null);
  const eliminatedRef = useRef(null);

  const [
    fitLayout,
    setFitLayout
  ] = useState({
    columns: 1,
    rowHeight: 220,
    scale: 1,
    placements: [],
    placementKey: ''
  });

  const [selectedStream, setSelectedStream] = useState(null); // null = all streams
  const [coursePlacementLabels, setCoursePlacementLabels] =
    useState({ streams: {}, groups: {} });

  const selectedCourse =
    courses.find(course => course.id === selectedCourseId) || null;

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    fetchCourses(session)
      .then(c => {
        if (cancelled) {
          return;
        }

        const loaded =
          Array.isArray(c)
            ? c
            : [];

        setCourses(loaded);

        if (loaded.length > 0) {
          setSelectedCourseId(
            loaded[0].id
          );

          setSelectedStream(
            loaded[0].streams_enabled === true
              ? 1
              : null
          );
        } else {
          setSelectedCourseId('');
          setSelectedStream(null);
          setWeeks([]);
          setQuestions([]);
        }
      })
      .catch(err => {
        if (cancelled) {
          return;
        }

        console.error(
          'Loading voting courses failed:',
          err
        );

        setCourses([]);
        setSelectedCourseId('');
        setWeeks([]);
        setQuestions([]);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  useEffect(() => {
    let cancelled = false;

    if (!selectedCourseId) {
      setCoursePlacementLabels({
        streams: {},
        groups: {}
      });
      return undefined;
    }

    fetchCoursePlacementLabels(
      selectedCourseId
    )
      .then(labels => {
        if (!cancelled) {
          setCoursePlacementLabels(
            labels || { streams: {}, groups: {} }
          );
        }
      })
      .catch(err => {
        console.error(
          'Loading Stream title failed:',
          err
        );
      });

    return () => {
      cancelled = true;
    };
    }, [
    selectedCourseId,
    selectedCourse?.cq_enabled
  ]);

  useEffect(() => {
    let cancelled = false;

    setWeeks([]);
    setQuestions([]);
    setWeekIndex(0);
    setEditingId(null);
    setEditText('');
    setShowAddForm(false);

    if (!selectedCourseId) {
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);

    fetchWeeksWithQuestions(
      selectedCourseId,
      selectedCourse?.cq_enabled === false
    )
      .then(w => {
        if (cancelled) return;

        const loadedWeeks = Array.isArray(w) ? w : [];
        setWeeks(loadedWeeks);

        const today = getEffectiveToday();
        const idx = loadedWeeks.findIndex(wk => wk.class_date >= today);
        setWeekIndex(
          loadedWeeks.length === 0
            ? 0
            : idx >= 0
              ? idx
              : loadedWeeks.length - 1
        );

        if (loadedWeeks.length === 0) {
          setQuestions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedCourseId,
    selectedCourse?.cq_enabled
  ]);

  useEffect(() => {
    let cancelled = false;
    const currentWeek = weeks[weekIndex];

    setQuestions([]);

    if (!currentWeek) {
      return () => {
        cancelled = true;
      };
    }

    const useRevisedQuestions = selectedCourse?.qi_enabled === true;

    fetchQuestions(currentWeek.id, useRevisedQuestions)
      .then(q => {
        if (cancelled) return;
        setQuestions(Array.isArray(q) ? q : []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load voting questions:', err);
        setQuestions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [weeks, weekIndex, selectedCourse?.qi_enabled]);

  function getDisplayQuestions() {
    let all = questions;

    if (selectedStream !== null) {
      all = all.filter(
        q => Number(q.stream) === Number(selectedStream)
      );
    }
    const active = all.filter(q => !q.is_eliminated);
    const eliminated = all.filter(q => q.is_eliminated);
    if (orderMode === 'votes') {
      active.sort((a, b) => {
        const aVotes = a.cq_votes?.[0]?.vote_count || a.cq_votes?.vote_count || 0;
        const bVotes = b.cq_votes?.[0]?.vote_count || b.cq_votes?.vote_count || 0;
        if (bVotes !== aVotes) return bVotes - aVotes;
        return 0;
      });
    }
    return { active, eliminated };
  }

  function getVoteCount(q) {
    return q.cq_votes?.[0]?.vote_count || q.cq_votes?.vote_count || 0;
  }

  function getVotingQuestionKey(
    question
  ) {
    return question?._isRevised
      ? 'qi:' +
        String(
          question._assignmentId
        )
      : 'cq:' +
        String(question?.id);
  }


  function getQuestionNumber(
    question
  ) {
    const streamQuestions =
      selectedStream === null
        ? questions
        : questions.filter(
            item =>
              Number(
                item.stream
              ) ===
              Number(
                selectedStream
              )
          );

    const targetKey =
      getVotingQuestionKey(
        question
      );

    const index =
      streamQuestions.findIndex(
        item =>
          getVotingQuestionKey(
            item
          ) === targetKey
      );

    return index >= 0
      ? index + 1
      : '';
  }


  async function handleVote(q, delta) {
    const current = getVoteCount(q);
    const newCount = Math.max(0, current + delta);
    const submissionId = q.id;
    await upsertVote(submissionId, newCount);
    setQuestions(prev => prev.map(item =>
      item.id === submissionId
        ? { ...item, cq_votes: [{ vote_count: newCount }] }
        : item
    ));
  }

  async function handleEliminate(q) {
    await updateEliminated(q.id, true);
    setQuestions(prev => prev.map(item =>
      item.id === q.id ? { ...item, is_eliminated: true } : item
    ));
  }

  async function handleRestore(q) {
    await updateEliminated(q.id, false);
    setQuestions(prev => prev.map(item =>
      item.id === q.id ? { ...item, is_eliminated: false } : item
    ));
  }

  async function handleDelete(q) {
    const warning =
      q?._isRevised === true
        ? (
            'Permanently delete this revised QI question? ' +
            'This deletes the submitted revised-question record and cannot be undone.'
          )
        : (
            'Permanently delete this question and its votes? ' +
            'This cannot be undone.'
          );

    if (
      !window.confirm(
        warning
      )
    ) {
      return;
    }

    try {
      await deleteVotingQuestionRecord(
        q
      );

      const targetKey =
        getVotingQuestionKey(q);

      setQuestions(
        previous =>
          previous.filter(
            item =>
              getVotingQuestionKey(
                item
              ) !== targetKey
          )
      );

    } catch (err) {
      console.error(
        'Deleting voting question failed:',
        err
      );

      window.alert(
        err?.message ||
        'Could not delete the question.'
      );
    }
  }


  async function handleClearVotes() {
    if (!window.confirm('Clear all votes and restore eliminated questions?')) return;
    if (!weeks[weekIndex]) return;
    await clearVotes(weeks[weekIndex].id);
    setQuestions(prev => prev.map(q => ({
      ...q,
      is_eliminated: false,
      cq_votes: [{ vote_count: 0 }]
    })));
    setOrderMode('submission');
  }

  async function handleAddQuestion(e) {
    e.preventDefault();
    if (!newQuestion.text.trim()) return;
    setSaving(true);
    try {
      const result = await saveQuestion(
        weeks[weekIndex].id,
        selectedCourseId,
        newQuestion.text.trim(),
        newQuestion.studentCode.trim(),
        selectedStream
      );

      setQuestions(prev => [
        ...prev,
        {
          ...result[0],
          stream: selectedStream,
          cq_votes: [{ vote_count: 0 }]
        }
      ]);
      setNewQuestion({ text: '', studentCode: '' });
      setShowAddForm(false);
    } finally {
      setSaving(false);
    }
  }

  async function handleEditSave(q) {
    if (!editText.trim()) return;
    await updateQuestion(q.id, editText.trim());
    setQuestions(prev => prev.map(item =>
      item.id === q.id ? { ...item, question_text: editText.trim() } : item
    ));
    setEditingId(null);
  }

  const { active, eliminated } = getDisplayQuestions();
  const votingLayoutSignature = active.map(q =>
    String(q.id) + ':' + String(q.question_text || '')
  ).join('\u0000');

  const currentWeek = weeks[weekIndex];


  /*
   * Fit active voting cards into the actual
   * available Cards area.
   *
   * We choose the row/column arrangement that
   * gives the largest usable cards, rather than
   * estimating size from question count alone.
   */
  useLayoutEffect(() => {
    const container =
      containerRef.current;

    if (
      !container ||
      active.length === 0
    ) {
      return undefined;
    }

    let frameId = null;

    function calculateFit() {
      if (frameId !== null) {
        window.cancelAnimationFrame(
          frameId
        );
      }

      frameId =
        window.requestAnimationFrame(
          () => {
            const current =
              containerRef.current;

            if (!current) {
              return;
            }

            const computed =
              window.getComputedStyle(
                current
              );

            const horizontalPadding =
              (
                parseFloat(
                  computed.paddingLeft
                ) || 0
              ) +
              (
                parseFloat(
                  computed.paddingRight
                ) || 0
              );

            const verticalPadding =
              (
                parseFloat(
                  computed.paddingTop
                ) || 0
              ) +
              (
                parseFloat(
                  computed.paddingBottom
                ) || 0
              );

            const eliminatedHeight =
              eliminatedRef.current
                ? eliminatedRef.current
                    .getBoundingClientRect()
                    .height + 12
                : 0;

            const width =
              Math.max(
                1,
                current.clientWidth -
                  horizontalPadding
              );

            const height =
              Math.max(
                1,
                current.clientHeight -
                  verticalPadding -
                  eliminatedHeight
              );

            const count =
              active.length;

            const gap = 12;

            let best = null;

            /*
             * 360 × 180 is roughly the preferred
             * voting-card shape. Try different
             * column counts and choose the layout
             * that preserves the greatest scale.
             */
            const maxColumns =
              Math.min(
                count,
                8
              );

            for (
              let columns = 1;
              columns <= maxColumns;
              columns += 1
            ) {
              const rows =
                Math.ceil(
                  count / columns
                );

              const cardWidth =
                (
                  width -
                  gap *
                    (columns - 1)
                ) /
                columns;

              if (cardWidth < 170) {
                continue;
              }

              const rawRowHeight =
                (
                  height -
                  gap *
                    (rows - 1)
                ) /
                rows;

              if (rawRowHeight <= 0) {
                continue;
              }

              /*
               * Prevent one or two cards becoming
               * absurdly tall — this was the old
               * follow-on problem.
               */
              const rowHeight =
                Math.min(
                  220,
                  rawRowHeight
                );

              const scale =
                Math.min(
                  1,
                  cardWidth / 360,
                  rowHeight / 180
                );

              if (
                !best ||
                scale > best.scale
              ) {
                best = {
                  columns,
                  rows,
                  rowHeight,
                  scale
                };
              }
            }

            if (!best) {
              const rows =
                active.length;

              best = {
                columns: 1,
                rows,
                rowHeight:
                  Math.max(
                    50,
                    (
                      height -
                      gap *
                        (rows - 1)
                    ) /
                    rows
                  ),
                scale: 0.35
              };
            }

            const next = {
              columns:
                best.columns,

              rowHeight:
                Math.max(
                  50,
                  Math.floor(
                    best.rowHeight
                  )
                ),

              scale:
                Math.max(
                  0.35,
                  Math.min(
                    1,
                    best.scale
                  )
                )
            };

            // Do not enlarge cards at the expense of the
            // existing card sizes or the available board area.
            const availableRows = Math.max(
              1,
              Math.floor((height + gap) / (next.rowHeight + gap))
            );

            const cardWidth = (
              width - gap * (next.columns - 1)
            ) / next.columns;

            const cardNodes = Array.from(
              gridRef.current?.querySelectorAll(
                '[data-voting-fit-card="true"]'
              ) || []
            );

            const proposals = [];

            if (cardNodes.length === active.length) {
              active.forEach((question, index) => {
                if (editingId === question.id) return;

                const card = cardNodes[index];

                const normal = measureVotingCardFont(
                  card,
                  cardWidth,
                  next.rowHeight,
                  next.scale
                );

                // Minimum desirable size for ordinary cards.
                if (normal >= 20) return;

                const enlarged = measureVotingCardFont(
                  card,
                  cardWidth,
                  next.rowHeight * 2 + gap,
                  next.scale
                );

                // Expansion must make a noticeable improvement.
                if (enlarged >= normal + 3) {
                  proposals.push({
                    id: String(question.id),
                    normal,
                    improvement: enlarged - normal
                  });
                }
              });
            }

            // Give the smallest questions priority.
            proposals.sort((a, b) =>
              a.normal - b.normal ||
              b.improvement - a.improvement
            );

            let selected = new Set();
            let placements = null;

            for (const proposal of proposals) {
              const trial = new Set(selected);
              trial.add(proposal.id);

              const trialPositions = planVotingTallPositions(
                active,
                next.columns,
                availableRows,
                trial
              );

              if (trialPositions) {
                selected = trial;
                placements = trialPositions;
              }
            }

            next.placements = placements || [];

            next.placementKey = next.placements.map(position =>
              String(position.id) + ':' +
              position.row + ':' +
              position.column + ':' +
              position.span
            ).join('|');



            setFitLayout(
              previous => {
                if (
                  previous.columns ===
                    next.columns &&
                  previous.rowHeight ===
                    next.rowHeight &&
                  Math.abs(
                    previous.scale -
                    next.scale
                  ) < 0.01 &&
                  previous.placementKey === next.placementKey
                ) {
                  return previous;
                }

                return next;
              }
            );
          }
        );
    }

    calculateFit();

    const observer =
      typeof ResizeObserver !==
      'undefined'
        ? new ResizeObserver(
            calculateFit
          )
        : null;

    observer?.observe(
      container
    );

    window.addEventListener(
      'resize',
      calculateFit
    );

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(
          frameId
        );
      }

      observer?.disconnect();

      window.removeEventListener(
        'resize',
        calculateFit
      );
    };
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    active.length,
    eliminated.length,
    showAddForm,
    selectedCourseId,
    weekIndex,
    selectedStream,
    votingLayoutSignature,
    editingId,
    visibleOrigins
  ]);


  /*
   * Keep the card geometry, but size each question independently.
   * A short question can grow into its available space; a long
   * question cannot shrink the text on any other card.
   */
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;

    let frameId = null;

    const fitQuestions = () => {
      const cards = grid.querySelectorAll('[data-voting-fit-card="true"]');

      cards.forEach(card => {
        const text = card.querySelector('[data-voting-fit-text="true"]');
        if (!text || !text.clientWidth || !text.clientHeight) return;

        const maxSize = Math.max(1, Math.min(
          64,
          Math.floor(text.clientWidth / 5),
          Math.floor(card.clientHeight * 0.35)
        ));

        let low = 1;
        let high = maxSize;
        let best = 1;

        // Find the largest font that fits THIS card.
        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          text.style.fontSize = mid + 'px';

          const fits =
            text.scrollHeight <= text.clientHeight + 1 &&
            text.scrollWidth <= text.clientWidth + 1;

          if (fits) {
            best = mid;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }

        // Every question fits within its card without scrolling.
        text.style.fontSize = best + 'px';
      });
    };

    const scheduleFit = () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        fitQuestions();
      });
    };

    fitQuestions();

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleFit)
      : null;

    if (observer) {
      observer.observe(grid);
      grid.querySelectorAll('[data-voting-fit-card="true"]')
        .forEach(card => observer.observe(card));
    }

    window.addEventListener('resize', scheduleFit);

    return () => {
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [
    active,
    editingId,
    fitLayout.columns,
    fitLayout.rowHeight,
    visibleOrigins
  ]);


  if (loading) return (
    <div style={styles.loadingWrap}>
      <div style={styles.loadingText}>Loading...</div>
    </div>
  );

  return (
    <div style={styles.wrap}>
      {/* Top bar */}
      <div style={styles.topBar}>
        <div style={styles.topLeft}>
          <select
            style={styles.courseSelect}
            value={getCourseSelectorValue(
              selectedCourseId,
              selectedStream
            )}
            onChange={e => {
              const next = parseCourseSelectorValue(
                e.target.value
              );

              setSelectedCourseId(next.courseId);
              setSelectedStream(next.stream);
            }}
          >
            {courses.flatMap(course => {
              const courseName = getCourseName(course);

              if (course.streams_enabled === true) {
                return [1, 2].map(stream => (
                  <option
                    key={`${course.id}-stream-${stream}`}
                    value={getCourseSelectorValue(
                      course.id,
                      stream
                    )}
                  >
                    {courseName} — {getStreamDisplayName(
                      coursePlacementLabels,
                      stream
                    )}
                  </option>
                ));
              }

              return [
                <option
                  key={course.id}
                  value={getCourseSelectorValue(
                    course.id,
                    null
                  )}
                >
                  {courseName}
                </option>
              ];
            })}
          </select>

          <div style={styles.weekNav}>
            <button
              style={styles.navBtn}
              disabled={weekIndex <= 0}
              onClick={() => setWeekIndex(i => i - 1)}
            >
              ‹
            </button>
            <span style={styles.weekLabel}>
              {currentWeek ? `Week ${currentWeek.week_number} · ${formatDate(currentWeek.class_date)}` : 'No weeks'}
            </span>
            <button
              style={styles.navBtn}
              disabled={weekIndex >= weeks.length - 1}
              onClick={() => setWeekIndex(i => i + 1)}
            >
              ›
            </button>
          </div>
        </div>

        <div style={styles.topRight}>
          
          <button
            style={styles.controlBtn}
            onClick={() => setOrderMode(m => m === 'submission' ? 'votes' : 'submission')}
          >
            {orderMode === 'submission' ? '↕ Sort by votes' : '↕ Sort by order'}
          </button>
          <button
            style={styles.controlBtn}
            onClick={() => setShowAddForm(f => !f)}
          >
            + Add question
          </button>
          <button
            style={{ ...styles.controlBtn, ...styles.dangerBtn }}
            onClick={handleClearVotes}
          >
            ↺ Clear votes
          </button>
        </div>
      </div>

      {/* Add question form */}
      {showAddForm && (
        <div style={styles.addForm}>
          <form onSubmit={handleAddQuestion} style={styles.addFormInner}>
            <input
              style={styles.addInput}
              type="text"
              value={newQuestion.text}
              onChange={e => setNewQuestion(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Question text..."
              autoFocus
            />
            <input
              style={{ ...styles.addInput, maxWidth: '160px' }}
              type="text"
              value={newQuestion.studentCode}
              onChange={e => setNewQuestion(prev => ({ ...prev, studentCode: e.target.value }))}
              placeholder="Student ID (optional)"
            />
            <button style={styles.addSubmitBtn} disabled={saving}>
              {saving ? 'Adding...' : 'Add'}
            </button>
            <button
              type="button"
              style={styles.addCancelBtn}
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      {/* Cards grid */}
      <div style={styles.cardsArea} ref={containerRef}>
        {active.length === 0 && (
          <div style={styles.emptyState}>No questions for this week.</div>
        )}
        <div
          ref={gridRef}
          style={{
            ...styles.cardsGrid,

            gridTemplateColumns:
              `repeat(${fitLayout.columns}, minmax(0, 1fr))`,

            gridAutoRows:
              `${fitLayout.rowHeight}px`
          }}
        >
          {active.map((q, i) => {
            const votes = getVoteCount(q);
            const cardNumber = getQuestionNumber(q);
            const isEditing = editingId === q.id;

            const questionKey =
              getVotingQuestionKey(q);

            const originVisible =
              visibleOrigins[
                questionKey
              ] === true;

            const cardPlacement = fitLayout.placements?.find(
              position => String(position.id) === String(q.id)
            ) || null;

            return (
              <div
                key={q.id}
                data-voting-fit-card="true"
                style={{
                  ...styles.card,

                  height: (
                    cardPlacement?.span === 2
                      ? fitLayout.rowHeight * 2 + 12
                      : fitLayout.rowHeight
                  ) + 'px',

                  gridRow: cardPlacement
                    ? String(cardPlacement.row) + ' / span ' +
                      cardPlacement.span
                    : undefined,

                  gridColumn: cardPlacement
                    ? String(cardPlacement.column)
                    : undefined,

                  padding:
                    `${Math.max(
                      5,
                      Math.round(
                        16 *
                        fitLayout.scale
                      )
                    )}px`
                }}
                onClick={() => !isEditing && handleVote(q, 1)}
              >
                {/* Watermark number */}
                <div style={styles.cardNumber}>{cardNumber}</div>

                {/* Vote count */}
                <div
                  style={{
                    ...styles.voteCount,

                    fontSize:
                      `${Math.max(22, Math.min(48, Math.round(fitLayout.rowHeight * 0.2)))}px`
                  }}
                >
                  {votes}
                </div>

                {/* Question text */}
                {isEditing ? (
                  <textarea
                    style={styles.editTextarea}
                    value={editText}
                    onChange={e => setEditText(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <div
                    data-voting-fit-text="true"
                    style={styles.questionText}
                  >
                    {q.question_text}
                  </div>
                )}

                {q._isRevised &&
                  originVisible &&
                  q._originName && (
                  <div
                    style={
                      styles.originReveal
                    }
                  >
                    Original question by: {q._originName}
                  </div>
                )}

                {/* Controls */}
                <div
                  style={{
                    ...styles.cardControls,

                    gap:
                      `${Math.max(
                        3,
                        Math.round(
                          6 *
                          fitLayout.scale
                        )
                      )}px`,

                    marginTop:
                      `${Math.max(
                        4,
                        Math.round(
                          10 *
                          fitLayout.scale
                        )
                      )}px`
                  }}
                  onClick={
                    e =>
                      e.stopPropagation()
                  }
                >
                  {isEditing ? (
                    <>
                      <button style={styles.cardBtn} onClick={() => handleEditSave(q)}>✓</button>
                      <button style={styles.cardBtn} onClick={() => setEditingId(null)}>✕</button>
                    </>
                  ) : (
                    <>
                      {q._isRevised &&
                        q._originName && (
                        <button
                          type="button"
                          style={{
                            ...styles.cardBtn,
                            width: 'auto',
                            padding: '0 8px',
                            fontSize: '11px'
                          }}
                          onClick={() =>
                            setVisibleOrigins(
                              previous => ({
                                ...previous,

                                [questionKey]:
                                  !previous[
                                    questionKey
                                  ]
                              })
                            )
                          }
                          title="Show original question author"
                        >
                          Origin
                        </button>
                      )}

                      <button
                        style={styles.cardBtn}
                        onClick={() => handleVote(q, -1)}
                        title="Remove vote"
                      >−</button>
                      <button
                        style={styles.cardBtn}
                        onClick={() => { setEditingId(q.id); setEditText(q.question_text); }}
                        title="Edit"
                      >✏️</button>
                      <button
                        style={{ ...styles.cardBtn, ...styles.eliminateBtn }}
                        onClick={() => handleEliminate(q)}
                        title="Eliminate"
                      >×</button>
                      <button
                        type="button"
                        style={{
                          ...styles.cardBtn,
                          borderColor:
                            '#ff3b30',
                          color:
                            '#ff3b30'
                        }}
                        onClick={() =>
                          handleDelete(q)
                        }
                        title="Delete permanently"
                        aria-label="Delete permanently"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Eliminated chips */}
        {eliminated.length > 0 && (
          <div
            ref={eliminatedRef}
            style={styles.eliminatedRow}
          >
            {eliminated.map(q => {
              const cardNumber = getQuestionNumber(q);
              return (
                <button
                  key={q.id}
                  style={styles.chip}
                  onClick={() => handleRestore(q)}
                  title="Restore question"
                >
                  {cardNumber}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    height: '100dvh',
    maxHeight: '100dvh',
    minHeight: 0,
    background: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  loadingWrap: {
    minHeight: '100vh', background: '#1a1a2e',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  loadingText: { color: 'white', fontSize: '18px' },
  topBar: {
    background: '#16213e', padding: '12px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid #0f3460', flexShrink: 0
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  topRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  courseSelect: {
    background: '#0f3460', color: 'white', border: '1px solid #e94560',
    borderRadius: '6px', padding: '6px 10px', fontSize: '14px', cursor: 'pointer'
  },
  weekNav: { display: 'flex', alignItems: 'center', gap: '8px' },
  navBtn: {
    background: '#0f3460', color: 'white', border: '1px solid #e94560',
    borderRadius: '6px', width: '32px', height: '32px', fontSize: '18px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  weekLabel: { color: 'white', fontSize: '14px', fontWeight: '600', minWidth: '160px', textAlign: 'center' },
  controlBtn: {
    background: '#0f3460', color: 'white', border: '1px solid #e94560',
    borderRadius: '6px', padding: '6px 12px', fontSize: '13px',
    cursor: 'pointer', fontWeight: '600'
  },
  dangerBtn: { borderColor: '#ff6b6b', color: '#ff6b6b' },
  controlBtnActive: { background: '#e94560', borderColor: '#e94560', color: 'white' },
  addForm: {
    background: '#16213e', padding: '12px 20px',
    borderBottom: '1px solid #0f3460', flexShrink: 0
  },
  addFormInner: { display: 'flex', gap: '10px', alignItems: 'center' },
  addInput: {
    flex: 1, background: '#0f3460', color: 'white', border: '1px solid #e94560',
    borderRadius: '6px', padding: '8px 12px', fontSize: '14px'
  },
  addSubmitBtn: {
    background: '#e94560', color: 'white', border: 'none',
    borderRadius: '6px', padding: '8px 16px', fontSize: '14px',
    fontWeight: '600', cursor: 'pointer'
  },
  addCancelBtn: {
    background: 'transparent', color: '#aaa', border: '1px solid #444',
    borderRadius: '6px', padding: '8px 12px', fontSize: '14px', cursor: 'pointer'
  },
  cardsArea: {
    flex: 1,
    minHeight: 0,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    overflow: 'hidden'
  },

  cardsGrid: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    columnGap: '12px',
    rowGap: '12px',
    alignContent: 'start'
  },
  emptyState: {
    color: '#888', fontSize: '18px', textAlign: 'center', padding: '40px'
  },
  card: {
    background: '#16213e',
    border: '2px solid #0f3460',
    borderRadius: '12px',
    padding: '16px',
    cursor: 'pointer',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    transition: 'border-color 0.15s',
    userSelect: 'none'
  },
  cardNumber: {
    position: 'absolute', top: '50%', left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '9vw', fontWeight: '900', color: '#0f3460',
    lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
    zIndex: 0, whiteSpace: 'nowrap'
  },
  voteCount: {
    fontSize: '36px', fontWeight: '900', color: '#e94560',
    textAlign: 'right', lineHeight: 1, position: 'relative', zIndex: 1
  },
  questionText: {
    color: 'white',
    lineHeight: '1.32',
    position: 'relative',
    zIndex: 1,
    marginTop: '8px',
    fontWeight: '500',
    flex: 1,
    minHeight: 0,
    overflowY: 'hidden',
    overflowX: 'hidden',
    overflowWrap: 'anywhere'
  },
  editTextarea: {
    width: '100%', background: '#0f3460', color: 'white',
    border: '1px solid #e94560', borderRadius: '6px',
    padding: '8px', fontSize: '14px', flex: 1, resize: 'none',
    position: 'relative', zIndex: 2
  },
  originReveal: {
    marginTop: '10px',
    padding: '6px 9px',
    borderRadius: '6px',
    background: '#0f3460',
    color: '#cbd5e1',
    fontSize: '12px',
    fontWeight: '600',
    position: 'relative',
    zIndex: 2
  },

  cardControls: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'flex-end',
    marginTop: '10px',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0
  },
  cardBtn: {
    background: '#0f3460', color: 'white', border: '1px solid #333',
    borderRadius: '4px', width: '28px', height: '28px', fontSize: '14px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
  },
  eliminateBtn: { borderColor: '#ff6b6b', color: '#ff6b6b' },
  eliminatedRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    flexShrink: 0,
    paddingTop: '8px',
    borderTop: '1px solid #0f3460'
  },
  chip: {
    background: '#0f3460', color: '#888', border: '1px solid #333',
    borderRadius: '6px', width: '40px', height: '40px', fontSize: '16px',
    fontWeight: '700', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center'
  }
};